import { MessageSquare, Video, Stethoscope, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

export default function DoctorsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const res = await api.get("/api/doctor");
        const data = res.data;
        const doctorsArray = Array.isArray(data)
          ? data
          : Array.isArray(data?.doctors)
          ? data.doctors
          : [];
        setDoctors(doctorsArray);
      } catch (err) {
        console.error("Error fetching doctors:", err);
        setDoctors([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, []);

  const specialty = new URLSearchParams(location.search).get("specialty") || "";
  const visibleDoctors = specialty
    ? doctors.filter((doctor) => String(doctor.specialty || "").toLowerCase().includes(specialty.toLowerCase()))
    : doctors;

  return (
    <section style={styles.page} className = "container">
    <FeatureHeader 
      title="All Doctors"
      subtitle={specialty ? `${specialty} doctors for your symptom check` : "Find a doctor near you"}
      onBack = {() => navigate(-1)}
    />
      {loading ? (
    
      
          <Loader />

      ) : visibleDoctors.length === 0 ? (
        <div style={styles.emptyState}>{specialty ? `No ${specialty} doctors found yet. Try a General Practitioner.` : "No doctors found"}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {visibleDoctors.map((doctor, index) => (
            <DoctorCard key={doctor._id || index} doctor={doctor} />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </section>
  );
}

function DoctorCard({ doctor }) {
  const navigate = useNavigate();
  const isOnline = (doctor.status || "").toLowerCase().includes("online");
  const rating = doctor.rating ?? 4.8;
  const reviews = doctor.reviews ?? 320;
  const fullStars = Math.floor(rating);
  const halfStar = rating - fullStars >= 0.5;

  return (
    <div
      onClick={() => navigate(`/doctor-details/${doctor._id}`)}

      style={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        minWidth: 0,
        maxWidth: "none",
        flexShrink: 0,
        backgroundColor: "#ffffff",
        borderRadius: 18,
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(2,6,23,0.06)",
        border: "1px solid #eef0f4",
        padding: "12px 12px 10px",
        gap: 12,
        cursor: "pointer",
      }}
    >
      <div style={{ width: 82, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <img
          src={doctor.avatar || "https://randomuser.me/api/portraits/men/7.jpg"}
          alt={doctor.name || "Doctor"}
          style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", display: "block" }}
        />
        <span style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: isOnline ? "#16a34a" : "#6b7280" }}>
          {isOnline ? "Online" : "Offline"}
        </span>
      </div>

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doctor.name || "Dr. Unknown"}
        </span>
        <div
          style={{
            fontSize: 11,
            color: "#4b5563",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {doctor.specialty || "General Practitioner"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <span style={{ color: "#f59e0b", fontSize: 12, letterSpacing: 0.5 }}>
            {"★".repeat(fullStars)}{halfStar ? "☆" : ""}{"☆".repeat(Math.max(0, 5 - fullStars - (halfStar ? 1 : 0)))}
          </span>
          <span style={{ fontSize: 11, color: "#374151", fontWeight: 600 }}>{Number(rating).toFixed(1)} ({reviews})</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <MessageSquare size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Chat</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Video size={16} color="#6b7280" />
            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Video</span>
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/doctor-details/${doctor._id}`);
          }}
          style={{
            marginTop: 8,
            width: "100%",
            background: "#f5f3ff",
            color: "#312e81",
            border: "1px solid #ece8ff",
            borderRadius: 14,
            padding: "8px 12px",
            fontSize: 12,
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <MessageSquare size={14} />
          Consult Now
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "var(--bg)",
    fontFamily: "'Poppins', sans-serif",
   
    position: "relative",
  },
  backButton: {
    position: "fixed",
    top: 20,
    left: 16,
    background: "rgba(255,255,255,0.2)",
    borderRadius: "50%",
    padding: "12px",
    fontSize: "20px",
    color: "#333",
    display: "inline-flex",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    cursor: "pointer",
    transition: "background 0.2s ease",
    zIndex: 100,
  },
  header: {
    marginBottom: 20,
    textAlign: "left",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#2e1065",
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: 6,
  },
  loaderWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    color: "#6b7280",
    padding: "24px 0",
  },
  emptyState: {
    textAlign: "center",
    color: "#6b7280",
    padding: "24px 0",
  },
};
